import { customElement, property } from 'lit/decorators.js';

import { ChartBase } from './base';

@customElement('echarts-line')
export class LineChart extends ChartBase {
  @property({ type: Boolean, attribute: true, reflect: true })
  smooth: boolean = false;

  @property({ type: Boolean, attribute: 'show-legend', reflect: true })
  showLegend: boolean = false;

  @property({ type: String, attribute: 'value-suffix' })
  valueSuffix: string = '';

  @property({ type: Number, attribute: 'fix-value' })
  fixValue: number = 0;

  @property({ type: String, attribute: 'x-key' })
  xKey: string = 'name';

  @property({ type: String, attribute: 'x-label' })
  xLabel: string = 'Name';

  @property({ type: String, attribute: 'y-keys' })
  yKeys: string = 'value';

  @property({ type: String, attribute: 'y-labels' })
  yLabels: string = 'Value';

  @property({ type: Number, attribute: 'y-min' })
  yMin: number | string = 0;

  @property({ type: Number, attribute: 'y-max' })
  yMax: number | string = 'auto';

  @property({ type: String, attribute: 'colors' })
  colors: string = '';

  @property({ type: Object, attribute: true, reflect: true })
  override data: object[] = [];

  @property({ type: Object, attribute: 'area-style', reflect: true })
  areaStyle: object | undefined = undefined;

  override disconnectedCallback() {
    super.disconnectedCallback();
  }

  override firstUpdated(changedProperties: Map<PropertyKey, unknown>) {
    super.firstUpdated(changedProperties);
  }

  override attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ): void {
    super.attributeChangedCallback(name, _old, value);
    this.requestUpdate();
  }

  override get options() {
    if (this.loading) {
      return this.loadingOptions;
    }
    const data = this.data;
    const xData = this.data.map((n) => n[this.xKey]);
    const yData = this.yKeys.split(',').map((k, i) => {
      const l = this.yLabels.split(',')[i] || k;
      const color = this.colors.split('|')[i];
      return {
        name: l,
        type: 'line',
        smooth: this.smooth,
        showSymbol: false,
        areaStyle: this.areaStyle,
        itemStyle: {
          color,
        },
        data: data.map((n: object) => n[k]),
      };
    });

    return {
      tooltip: {
        trigger: 'axis',
        valueFormatter: (value: number) =>
          `${Number(value).toFixed(this.fixValue)}${this.valueSuffix}`,
      },
      legend: {
        show: this.showLegend,
      },
      toolbox: {
        show: false,
      },
      grid: {
        x: 80,
        x2: 80,
      },
      xAxis: [
        {
          type: 'category',
          boundaryGap: false,
          data: xData,
        },
      ],
      yAxis: [
        {
          type: 'value',
          min: Number.isNaN(this.yMin) ? undefined : this.yMin,
          max: Number.isNaN(this.yMax) ? undefined : this.yMax,
        },
      ],
      series: yData,
    };
  }

  setData(data: object[]) {
    this.data = data;
    this.requestUpdate();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'echarts-line': LineChart;
  }
}
